import React from 'react'
import styled from 'styled-components'

import { mediaQuery } from '@hozana/styling/responsive'

import { Block, TBlockProps } from 'elements/layout/Block'
import { BorderedDiv } from 'elements/layout/BorderedDiv'
import { Cell } from 'elements/layout/Cell'
import { Container } from 'elements/layout/Container'
import { Div } from 'elements/layout/Div'
import { PreloadDiv } from 'elements/layout/PreloadDiv'
import { RoundDiv } from 'elements/layout/RoundDiv'
import { Row } from 'elements/layout/Row'
import { Shadow } from 'elements/layout/Shadow'
import { Space } from 'elements/layout/Space'
import { StretchCell } from 'elements/layout/StretchCell'
import { Separator } from 'elements/ui/Separator'

import { Tabs } from 'general/structure/Tabs'
import { PreloadTabsBar } from 'general/structure/Tabs/PreloadTabsBar'

import { PreloadPublicationBlock } from 'modules/publication/components/PublicationBlock/PreloadPublicationBlock'

const StyledBlock = styled(Block)<TBlockProps>`
  z-index: 1;
  ${mediaQuery('xs', 'xs')} {
    box-shadow: none;
  }
`

export type TPreloadCommunityPageProps = {
  tabList: any
}

export const PreloadCommunityPage: TFC<TPreloadCommunityPageProps> = ({ tabList }) => (
  <Tabs data={tabList}>
    {({ currentTab }) => (
      <>
        <Container p={{ xs: '0', lg: '20px 10px 0' }}>
          {/* title */}
          <PreloadDiv h="58px" m="10px 0 20px" visible={['lg', 'xl']} />
          <StyledBlock square noPrint>
            {/* Community picture */}
            <BorderedDiv noOverflow noBorder borderRadius={{ md: '10px 10px 0 0' }} align="middleCenter">
              <PreloadDiv
                bg="white"
                maxW="100%"
                w="990px"
                h="calc(100vw / 2.32)" // force to keep the proportion 990px x 427px
                maxH="427px"
              >
                <Shadow inset depth="8px" x="0" y="-12px" opacity="0.3" spread="-8px" w="100%" h="100%" />
              </PreloadDiv>
            </BorderedDiv>
            <Div p="10px 10px 0px" m="auto" maxW={{ xs: 'initial', md: '712px', lg: 'initial' }}>
              <Row gutter="20px">
                {/* Avatar */}
                <Cell invisible={['lg', 'xl']}>
                  <PreloadDiv as={RoundDiv} size="60px" />
                </Cell>
                {/* Main column in the top block : title, description, ... */}
                <StretchCell align="middleBetween" column p="10px 10px 0 !important">
                  {/* Wrapper 1 used by middleBetween to push the TabsBar on the bottom of the block */}
                  <Div w="100%">
                    {/* Title */}
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="50%" />
                    {/* Name and roles of the user : creator, administrator, animator */}
                    <Div invisible={['lg', 'xl']}>
                      <PreloadDiv h="1em" w="100px" />
                    </Div>
                    {/* Main informations desktop : description */}
                    <Div visible={['lg', 'xl']}>
                      {/* Community introduction only visible on desktop */}
                      <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                      <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                      <PreloadDiv h="20px" m="0 0 5px" w="50%" />
                    </Div>
                    <Div visible={['lg', 'xl']} m="1em 0">
                      {/* frequency */}
                      <PreloadDiv h="1em" w="200px" />
                      {/* dates */}
                      <PreloadDiv h="1em" w="200px" />
                    </Div>
                  </Div>
                  {/* Wrapper 2 used by middleBetween to push the TabsBar on the bottom of the block */}
                  <Div w="100%">
                    {/* TabsBar on desktop */}
                    <Div visible={['lg', 'xl']} m="0 -10px">
                      <PreloadTabsBar />
                    </Div>
                  </Div>
                </StretchCell>
                {/* The following <Cell /> replicate the gutter between main column and sidebar on large screen */}
                <Cell w="20px" visible={['lg', 'xl']} />
                {/* Main actions on community page : join / leave... */}
                <Cell
                  // 260px corresponds to the sidebar width
                  w={{ xs: '100%', lg: '260px' }}
                  align="middleCenter"
                >
                  <Div w="100%">
                    <Space h={{ xs: '10px', lg: '0px' }} />
                    <Div m={{ xs: '0 0 0 12px', lg: 'auto' }} align="center" w={{ xs: 'fit-content', lg: 'auto' }}>
                      <PreloadDiv h="20px" w="50%" />
                    </Div>
                    <Space h={{ xs: '10px', lg: '0px' }} />
                    <Div invisible={['lg', 'xl']}>
                      {/* frequency */}
                      <PreloadDiv h="1em" w="200px" />
                      {/* dates */}
                      <PreloadDiv h="1em" w="200px" />
                    </Div>
                    <Space h="1em" />
                    {/* join button */}
                    <PreloadDiv h="50px" />
                  </Div>
                </Cell>
              </Row>
            </Div>
            {/* TabsBar on mobile */}
            <Div invisible={['lg', 'xl']}>
              <Space />
              <PreloadTabsBar />
            </Div>
          </StyledBlock>

          <Row gutter="1em">
            <Cell flex="1" maxW="727px" minW="0px" m="0 auto">
              <Space noPrint />
              {currentTab === 'description' ? (
                <>
                  <Block square p="10px 0 5px 0" invisible={['md', 'lg', 'xl']}>
                    {/* Community description only visible on mobile */}
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="50%" />
                    <Space />
                    {/* tags */}
                    <Row>
                      <Cell>
                        <PreloadDiv h="31px" w="100px" />
                      </Cell>
                      <Cell>
                        <PreloadDiv h="31px" w="100px" />
                      </Cell>
                    </Row>
                    <Space />
                    {/* Shares */}
                    <Row>
                      <Cell>
                        <PreloadDiv h="1em" w="100px" />
                      </Cell>
                      <Cell align="middleLeft">
                        <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                        <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                        <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                      </Cell>
                    </Row>
                  </Block>
                  <Div m="1em 0" visible={['md', 'lg', 'xl']}>
                    <Row>
                      <Cell w={{ md: 3 / 6, lg: 5 / 9, xl: 7 / 12 }}>
                        {/* tags */}
                        <Row>
                          <Cell>
                            <PreloadDiv h="31px" w="100px" />
                          </Cell>
                          <Cell>
                            <PreloadDiv h="31px" w="100px" />
                          </Cell>
                        </Row>
                      </Cell>
                    </Row>
                  </Div>
                  <Div p={{ xs: '0 10px', md: '0' }} m="1em 0 0 0">
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="100%" />
                    <PreloadDiv h="20px" m="0 0 5px" w="50%" />
                  </Div>
                </>
              ) : (
                <>
                  <PreloadPublicationBlock />
                  <Space />
                  <PreloadPublicationBlock />
                  <Space />
                  <PreloadPublicationBlock />
                </>
              )}
            </Cell>
            <Cell invisible={['xs', 'sm']}>
              <Space />
              <Block square h="100%" p="10px 1em">
                {/* user info */}
                <Div p="10px 20px">
                  <PreloadDiv as={RoundDiv} size="60px" />
                  <PreloadDiv h="1em" m="0 0 6px" />
                  <PreloadDiv h="1em" m="0 0 6px" />
                </Div>
                <Separator color="black10" m="5px 0" />
                <Div align="center">
                  {/* members */}
                  <PreloadDiv h="25px" m="0 0 10px" />
                  {/* join button */}
                  <PreloadDiv h="50px" />
                </Div>
                <Space />
                <Separator color="black10" m="5px 0" />
                {/* shares */}
                <PreloadDiv h="21px" m="10px" />
                <Div align="middleCenter">
                  <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                  <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                  <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                  <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                </Div>
                <Div align="middleCenter" m="10px 10px 0">
                  <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                  <PreloadDiv as={RoundDiv} size="35px" m="3px 6px" />
                </Div>
              </Block>
            </Cell>
          </Row>
        </Container>
      </>
    )}
  </Tabs>
)
